import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

interface MediaImage {
  fileName: string;
  fileUrl: string;
}

interface MediaItem {
  id: string;
  title: string;
  date: string;
  images: MediaImage[];
  youtubeId?: string;
  uploadDate: string;
}

const MediaPageContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #2c5aa0 0%, #1e3a8a 100%);
  color: #fff;
  padding: 80px 0;
  text-align: center;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ContentSection = styled.section`
  padding: 80px 0;
  background: #fff;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const MediaGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 0 15px;
  }
`;

const MediaCard = styled.div`
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 15px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #2c5aa0;
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const CardThumbnail = styled.div<{ $bgUrl?: string }>`
  height: 200px;
  background: ${({ $bgUrl }) =>
    $bgUrl
      ? `url(${$bgUrl}) center/cover no-repeat`
      : 'linear-gradient(135deg, #2c5aa0 0%, #1e3a8a 100%)'};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageCount = styled.span`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 13px;
`;

const YouTubeBadge = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  background: #ff0000;
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

const CardContent = styled.div`
  padding: 20px;

  h3 {
    font-size: 18px;
    color: #2c5aa0;
    margin-bottom: 8px;
    line-height: 1.4;
  }

  .date {
    color: #888;
    font-size: 14px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 15px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  padding: 25px 30px 15px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h2 {
    font-size: 24px;
    color: #2c5aa0;
    margin: 0;
    padding-right: 20px;
  }

  .date {
    color: #888;
    font-size: 14px;
    margin-top: 5px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  color: #666;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 25px 30px 30px;
`;

const ModalImage = styled.img`
  width: 100%;
  border-radius: 8px;
  margin-bottom: 15px;
  display: block;
`;

const YouTubeEmbed = styled.div`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  margin-bottom: 15px;
  border-radius: 8px;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  width: 100%;
  padding: 40px;
  color: #666;
  font-size: 16px;
`;

const EmptyText = styled.div`
  text-align: center;
  width: 100%;
  padding: 40px;
  color: #666;
  font-size: 16px;
`;

const Media: React.FC = () => {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<MediaItem | null>(null);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const backendUrl = process.env.REACT_APP_API_URL || 'https://ijbc-backend-1089361061119.us-west1.run.app';
        const response = await fetch(`${backendUrl}/api/media`);
        const data = await response.json();

        if (data.success && data.media) {
          setMediaItems(data.media);
        }
      } catch (error) {
        console.error('Failed to fetch media:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, []);

  useEffect(() => {
    if (selectedItem) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedItem]);

  return (
    <MediaPageContainer>
      <HeroSection>
        <HeroContent>
          <Title>사진/영상</Title>
          <Subtitle>교회 활동 사진과 영상을 보실 수 있습니다</Subtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <MediaGrid>
          {loading ? (
            <LoadingText>Loading...</LoadingText>
          ) : mediaItems.length === 0 ? (
            <EmptyText>등록된 사진/영상이 없습니다.</EmptyText>
          ) : (
            mediaItems.map((item) => (
              <MediaCard key={item.id} onClick={() => setSelectedItem(item)}>
                <CardThumbnail
                  $bgUrl={item.images.length > 0 ? item.images[0].fileUrl : undefined}
                >
                  {item.youtubeId && <YouTubeBadge>YouTube</YouTubeBadge>}
                  {item.images.length > 1 && (
                    <ImageCount>{item.images.length} photos</ImageCount>
                  )}
                </CardThumbnail>
                <CardContent>
                  <h3>{item.title}</h3>
                  <span className="date">{item.date}</span>
                </CardContent>
              </MediaCard>
            ))
          )}
        </MediaGrid>
      </ContentSection>

      {selectedItem && (
        <ModalOverlay onClick={() => setSelectedItem(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <h2>{selectedItem.title}</h2>
                <div className="date">{selectedItem.date}</div>
              </div>
              <CloseButton onClick={() => setSelectedItem(null)}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              {selectedItem.youtubeId && (
                <YouTubeEmbed>
                  <iframe
                    src={`https://www.youtube.com/embed/${selectedItem.youtubeId}`}
                    title={selectedItem.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </YouTubeEmbed>
              )}
              {selectedItem.images.map((img, idx) => (
                <ModalImage
                  key={idx}
                  src={img.fileUrl}
                  alt={`${selectedItem.title} - ${idx + 1}`}
                  loading="lazy"
                />
              ))}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </MediaPageContainer>
  );
};

export default Media;
